import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import {FormControl} from '@angular/forms';
import {merge, Observable} from 'rxjs';
import {map, takeUntil, tap} from 'rxjs/operators';
import {
    DeliverableDepositFilter
} from '../../components/deliverable-input/deliverable-autocomplete/filters/deliverable-deposit-filter';
import {destroy$} from '../../components/destroyable';
import {DexSecuritiesSubjectService} from '../../components/dex/dex-streaming/subjects/dex-securities-subject-service';
import {isDeliverablesEqual} from '../../components/dex/dex-streaming/types/deliverable';
import {DEX_LP_TRADE_CONTEXT, DexLpTradeController} from '../../components/dex/lp-trade/dex-lp-trade-controller';
import {Widget} from '../../components/layout/widget/widget';
import {ModalService} from '../../components/modal';
import {ALERT_COMMON_STRINGS} from '../../components/modal/alert-modal';
import {Side} from '../../enums/side';
import {WidgetCommonAction} from '../../pages/dashboard/layout/widget';
import {getSettingsUpdateKeysDict} from '../components/common-widget.implementation';
import {bindControlToSettings} from '../components/widget-impl-settings-component-base';
import {WidgetInnerAlertComponent} from '../components/widget-inner-alert';
import {DexLpTradeWidget} from './dex-lp-trade-widget';
import {DexLpTradeWidgetRevealService} from './dex-lp-trade-widget-reveal.service';
import {DexLpTradeWidgetSettings} from './settings';
import {DexTradeService} from './trade-execution/dex-trade.service';
import {DexTxError} from './trade-execution/dex-tx-error';

@Component({
    selector:        'dex-lp-trade-widget',
    templateUrl:     './dex-lp-trade-widget.component.html',
    styleUrls:       ['./dex-lp-trade-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone:      false,
    viewProviders:   [
        DeliverableDepositFilter,
        DexTradeService,
        ModalService,
        {
            provide:    DEX_LP_TRADE_CONTEXT,
            useFactory: () => {
                const widget = inject(Widget) as DexLpTradeWidget;
                const wImpl = widget.implementationInstance;
                const settings = wImpl.settings;

                return {
                    tranche:     wImpl.$tranche,
                    side:        settings.side ?? Side.BUY,
                    initial:     {assetSymbol: settings.assetSymbol, qty: settings.qty},
                    onLateError: inject(DexLpTradeWidgetRevealService).createErrorHandler(widget)
                };
            }
        },
        DexLpTradeController
    ]
})
export class DexLpTradeWidgetComponent {

    @ViewChild('confirmModalAnchor', {read: ViewContainerRef, static: true})
    public readonly confirmModalAnchorViewContainerRef: ViewContainerRef;

    public readonly trade = inject(DexLpTradeController);
    public readonly destroy$ = destroy$();

    public readonly Side = Side;

    private readonly widget = inject(Widget) as DexLpTradeWidget;
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly dexSecuritiesSubjectService = inject(DexSecuritiesSubjectService);
    private readonly modalService = inject(ModalService);

    private readonly wImpl = this.widget.implementationInstance;

    constructor() {
        this.bindControlsToSettings();
        this.listenToSettings();
    }

    public async goToConfirmation(): Promise<void> {
        const err = await this.trade.execute();

        if (!err) {
            this.close();
        } else {
            if (this.trade.$side() === Side.BUY) {
                this.showDepositErrorModal(err);
            } else {
                this.showWithdrawalErrorModal(err);
            }
        }
    }

    public close(): void {
        this.widget.action$.next({
            type: WidgetCommonAction.REMOVE_WIDGET
        });
    }

    private bindControlsToSettings(): void {
        // settings: form <-> settings (desktop persistence); form -> signals is already done by the controller
        merge(
            bindControlToSettings({
                widget: this.widget,
                control: this.trade.buyForm.controls.asset,
                settingsFieldName: 'assetSymbol',
                convertValueFromSettingsToControl: symbol =>
                            this.dexSecuritiesSubjectService.getDeliverableBySymbol(symbol),
                convertValueFromControlToSettings: d => d?.symbol,
                controlValueEqualityPredicate: isDeliverablesEqual
            }),
            this.bindQtyToSettings(this.trade.buyForm.controls.qty),
            this.bindQtyToSettings(this.trade.sellForm.controls.qty)
        ).pipe(takeUntil(this.destroy$)).subscribe();
    }

    private listenToSettings(): void {
        this.widget.settingsWithUpdates$.pipe(
            map((values: Partial<DexLpTradeWidgetSettings>) => ({keys: getSettingsUpdateKeysDict(values), values})),
            tap(({keys, values}) => {
                if (keys.side) {
                    this.trade.setSide(values.side);
                }
                if (values.initialError) {
                    this.trade.setError(values.initialError);
                    this.wImpl.updateSettings({initialError: undefined}).subscribe();
                }
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private bindQtyToSettings(qtyControl: FormControl<number>): Observable<any> {
        return bindControlToSettings({
            widget:            this.widget,
            control:           qtyControl,
            settingsFieldName: 'qty',
            convertValueFromSettingsToControl:
                               qtyStr => qtyStr ? parseFloat(qtyStr) : null,
            convertValueFromControlToSettings:
                               qty => qty === null || qty === undefined ? undefined : String(qty)
        });
    }

    private showDepositErrorModal(e: DexTxError | Error | unknown): void {
        const eMsg = e instanceof DexTxError
            ? e.naming?.msg || e.details.name
            : undefined;

        this.modalService.open(WidgetInnerAlertComponent, {
            place:     {
                viewContainerRef: this.confirmModalAnchorViewContainerRef
            },
            configure: {
                title:            'Purchasing Failed', // TODO: Locale
                titleTranslate:   false,
                message:          eMsg || 'Please check and try again', // TODO: Locale
                messageTranslate: true,
                buttonText:       ALERT_COMMON_STRINGS.buttonOk
            }
        });
        this.changeDetectorRef.markForCheck();
    }

    private showWithdrawalErrorModal(e: DexTxError | Error | unknown): void {
        const eMsg = e instanceof DexTxError
            ? e.naming?.msg || e.details.name
            : undefined;

        this.modalService.open(WidgetInnerAlertComponent, {
            place:     {
                viewContainerRef: this.confirmModalAnchorViewContainerRef
            },
            configure: {
                title:            'Redemption Failed', // TODO: Locale
                titleTranslate:   false,
                message:          eMsg || 'Please check and try again', // TODO: Locale
                messageTranslate: true,
                buttonText:       ALERT_COMMON_STRINGS.buttonOk
            }
        });
        this.changeDetectorRef.markForCheck();
    }

}
